import { useCallback, useMemo, useState } from 'react';
import { DSButton, DSIcon, DSTextInput, DSTypography } from '@eiv2/design-system';
import { DataGrid, type GridColDef, type GridPaginationModel } from '@mui/x-data-grid';
import { CollapsibleFiltersCard } from '../CollapsibleFiltersCard';
import { DataGridFrame, visibleDataGridRows } from '../DataGridFrame';
import { DateRangeFields, DateRangeValidationMessage } from '../DateRangePicker';
import { IconTooltipButton } from '../IconTooltipButton';
import { LoadMoreButton } from '../LoadMoreButton';
import { RequestState } from '../RequestState';
import { SectionCard } from '../SectionCard';
import { usePaginatedList } from '../../hooks/usePaginatedList';
import { getDefaultWindow, type DateRange } from '../../services/dateWindow';
import { formatDateTime, formatNumber } from '../../services/format';
import { dataGridLocaleText } from '../../theme/dataGridLocale';
import type { OrganizerLeadParams } from '../../organizer/api';
import type { RemarketingLead, RemarketingLeadListResponse } from '../../types/api';
import {
  apiErrorCode,
  EventRequiredState,
  eventLabel,
  OrganizerEventFilter,
  type OrganizerEventScope
} from './OrganizerEventFilter';

/**
 * Leads da área do ORGANIZADOR: só consulta. Exportação e exclusão LGPD do
 * painel administrativo não têm equivalente de organizador — nem botão, nem
 * rota no BFF. PII (nome, e-mail, telefone) vive só no estado em memória desta
 * tela; os filtros que vão para a URL são IDs e datas.
 */
export interface OrganizerLeadsProps {
  listLeads: (params: OrganizerLeadParams, signal?: AbortSignal) => Promise<RemarketingLeadListResponse>;
  eventScope: OrganizerEventScope;
}

export function OrganizerLeads({ listLeads, eventScope }: OrganizerLeadsProps) {
  const [dateWindow, setWindow] = useState<DateRange>(() => getDefaultWindow());
  const [draft, setDraft] = useState({ eventId: '', source: '' });
  const [applied, setApplied] = useState({ eventId: '', source: '' });
  const [eventFilterRequired, setEventFilterRequired] = useState(false);
  // Remonta o filtro de evento (e o ID digitado) ao limpar.
  const [eventFilterKey, setEventFilterKey] = useState(0);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25
  });
  const requiresEvent = eventScope.eventsTruncated || eventFilterRequired;
  const blocked = requiresEvent && !applied.eventId;

  const filters = useMemo(
    () => ({
      eventId: applied.eventId || undefined,
      source: applied.source || undefined,
      createdFrom: dateWindow.from,
      createdTo: dateWindow.to
    }),
    [applied, dateWindow.from, dateWindow.to]
  );

  const fetcher = useCallback(
    async (cursor: string | null, signal: AbortSignal) => {
      if (blocked) return { data: [], nextCursor: null };
      try {
        const page = await listLeads({ ...filters, limit: 50, cursor }, signal);
        return { data: page.leads, nextCursor: page.nextCursor };
      } catch (err) {
        if (apiErrorCode(err) === 'event_filter_required') setEventFilterRequired(true);
        throw err;
      }
    },
    [filters, blocked, listLeads]
  );

  const list = usePaginatedList<RemarketingLead>(fetcher, [
    filters.eventId,
    filters.source,
    filters.createdFrom,
    filters.createdTo,
    blocked
  ]);

  const columns = useMemo<GridColDef<RemarketingLead>[]>(
    () => [
      { field: 'name', headerName: 'Nome', flex: 1, minWidth: 160 },
      { field: 'email', headerName: 'E-mail', flex: 1, minWidth: 200 },
      { field: 'phone', headerName: 'Telefone', width: 150 },
      {
        field: 'eventId',
        headerName: 'Evento',
        width: 200,
        renderCell: ({ row }) => eventLabel(eventScope.events, row.eventId)
      },
      { field: 'source', headerName: 'Origem', width: 130 },
      {
        field: 'createdAt',
        headerName: 'Capturado em',
        width: 170,
        valueFormatter: (value: string) => formatDateTime(value)
      }
    ],
    [eventScope.events]
  );

  function clearFilters() {
    setDraft({ eventId: '', source: '' });
    setEventFilterKey((key) => key + 1);
    setApplied({ eventId: '', source: '' });
  }

  return (
    <div className="flex flex-col gap-6">
      <CollapsibleFiltersCard storageKey="organizer-remarketing-leads" title="Filtrar leads">
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            setApplied({ eventId: draft.eventId.trim(), source: draft.source.trim() });
          }}
        >
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
              <DateRangeFields value={dateWindow} onChange={setWindow} />
              <OrganizerEventFilter
                key={eventFilterKey}
                onChange={(eventId) => setDraft({ ...draft, eventId })}
                required={requiresEvent}
                scope={eventScope}
                value={draft.eventId}
              />
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <DSTextInput
                classNames={{ field: 'min-h-14 bg-light-gray' }}
                label="Origem"
                onChange={(e) => setDraft({ ...draft, source: e.target.value })}
                value={draft.source}
              />
            </div>
            <DateRangeValidationMessage value={dateWindow} onChange={setWindow} />
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
            <DSButton className="w-full sm:w-auto" type="submit">
              <DSIcon name="Filter" size={16} />
              Filtrar
            </DSButton>
            <DSButton className="w-full sm:w-auto" onClick={clearFilters} type="button" variant="secondary">
              Limpar filtros
            </DSButton>
          </div>
        </form>
      </CollapsibleFiltersCard>

      {blocked ? (
        <EventRequiredState truncated={eventScope.eventsTruncated} />
      ) : list.error ? (
        <RequestState title="Não foi possível carregar os leads" error={list.error} onRetry={list.reload} />
      ) : list.isLoading && list.items.length === 0 ? (
        <RequestState title="Carregando leads" message="Buscando o período selecionado." />
      ) : (
        <SectionCard
          action={
            <IconTooltipButton disabled={list.isLoading} label="Atualizar" onClick={list.reload}>
              <DSIcon name="Refresh" size={20} />
            </IconTooltipButton>
          }
          subtitle={`${formatNumber(list.items.length)} ${list.items.length === 1 ? 'lead carregado' : 'leads carregados'}`}
          title="Leads"
        >
          {list.items.length === 0 ? (
            <DSTypography as="p" className="py-8 text-[13px] font-normal text-gray-5">
              Nenhum lead capturado com esses filtros.
            </DSTypography>
          ) : (
            <DataGridFrame
              visibleRowCount={visibleDataGridRows(
                list.items.length,
                paginationModel.page,
                paginationModel.pageSize
              )}
            >
              <DataGrid
                rows={list.items}
                columns={columns}
                disableColumnMenu
                disableRowSelectionOnClick
                localeText={{
                  ...dataGridLocaleText,
                  noRowsLabel: 'Nenhum lead encontrado com esses filtros.'
                }}
                pageSizeOptions={[10, 25, 50]}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
              />
            </DataGridFrame>
          )}
          <LoadMoreButton
            nextCursor={list.nextCursor}
            onLoadMore={list.loadMore}
            isLoading={list.isLoading || list.isLoadingMore}
          />
        </SectionCard>
      )}
    </div>
  );
}
